/**
 * BookChapterManagement
 * 
 * Admin UI for managing books and their chapters.
 * Gated by trainingBookChapters feature flag.
 */

import { useState, useMemo, useCallback } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  Collapsible, CollapsibleContent, CollapsibleTrigger,
} from '@/components/ui/collapsible';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Plus, ChevronDown, BookOpen, Loader2, Trash2, ListPlus, Search, Pencil,
} from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';
import { ArtifactCapabilityEditor } from '@/components/training/ArtifactCapabilityEditor';
import type { TrainingArtifact } from '@/types/training';

// =============================================
// TYPES
// =============================================

interface BookWithChapters extends TrainingArtifact {
  chapters: TrainingArtifact[];
}

interface ParsedChapter {
  number: number;
  title: string;
  summary: string;
}

// =============================================
// HOOKS
// =============================================

function useBooks() {
  return useQuery({
    queryKey: ['book-chapter-management-books'],
    queryFn: async () => {
      // Fetch all books
      const { data: books, error: bErr } = await supabase
        .from('training_artifacts')
        .select('*')
        .eq('artifact_type', 'book')
        .is('parent_artifact_id', null)
        .order('title');
      if (bErr) throw bErr;

      // Fetch all chapters
      const { data: chapters, error: cErr } = await supabase
        .from('training_artifacts')
        .select('*')
        .eq('artifact_type', 'chapter')
        .not('parent_artifact_id', 'is', null)
        .order('sort_index');
      if (cErr) throw cErr;

      const chaptersByParent = new Map<string, TrainingArtifact[]>();
      for (const ch of (chapters || []) as TrainingArtifact[]) {
        const pid = ch.parent_artifact_id!;
        if (!chaptersByParent.has(pid)) chaptersByParent.set(pid, []);
        chaptersByParent.get(pid)!.push(ch);
      }

      return ((books || []) as TrainingArtifact[]).map(b => ({
        ...b,
        chapters: chaptersByParent.get(b.id) || [],
      })) as BookWithChapters[];
    },
    staleTime: 30_000,
  });
}

// =============================================
// HELPERS
// =============================================

function parseChapterList(text: string): ParsedChapter[] {
  const lines = text.split('\n').map(l => l.trim()).filter(Boolean);
  const results: ParsedChapter[] = [];

  for (const line of lines) {
    // Match patterns like "1. Title", "1 - Title", "1: Title", "Chapter 1: Title"
    const match = line.match(/^(?:chapter\s+)?(\d+)[.\-:\s]+(.+)$/i);
    if (match) {
      results.push({
        number: parseInt(match[1], 10),
        title: match[2].trim(),
        summary: '',
      });
    }
  }

  return results;
}

function mappingStatusBadge(status: string | undefined) {
  switch (status) {
    case 'complete':
      return <Badge className="bg-green-500/15 text-green-700 border-green-200 dark:text-green-400">Complete</Badge>;
    case 'partial':
      return <Badge className="bg-amber-500/15 text-amber-700 border-amber-200 dark:text-amber-400">Partial</Badge>;
    default:
      return <Badge variant="secondary">Draft</Badge>;
  }
}

// =============================================
// MAIN COMPONENT
// =============================================

export function BookChapterManagement() {
  const { data: books, isLoading, refetch } = useBooks();
  const queryClient = useQueryClient();
  const [searchQuery, setSearchQuery] = useState('');
  const [addBookOpen, setAddBookOpen] = useState(false);
  const [generateChaptersBookId, setGenerateChaptersBookId] = useState<string | null>(null);
  const [deleteBookId, setDeleteBookId] = useState<string | null>(null);
  const [retireReason, setRetireReason] = useState('');
  const [capabilityEditorChapterId, setCapabilityEditorChapterId] = useState<string | null>(null);

  const filteredBooks = useMemo(() => {
    if (!books) return [];
    if (!searchQuery) return books;
    const q = searchQuery.toLowerCase();
    return books.filter(b =>
      b.title.toLowerCase().includes(q) ||
      b.authors?.toLowerCase().includes(q) ||
      b.isbn_13?.includes(q)
    );
  }, [books, searchQuery]);

  // Core-100 R4 CORE-O/CORE-S: "Retirement is not erasure." A book is RETIRED (no longer offered), never deleted —
  // deleting it cascaded every learner's history. The server governs and audits it (retire_training_artifact).
  const handleDeleteBook = async () => {
    if (!deleteBookId) return;
    const reason = retireReason.trim();
    if (!reason) {
      toast.error('A reason is required to retire a book');
      return;
    }
    const { error } = await supabase.rpc('retire_training_artifact' as never, {
      p_artifact_id: deleteBookId, p_reason: reason,
    } as never);
    if (error) {
      toast.error('Failed to retire book');
    } else {
      toast.success('Book and its chapters retired. Learners keep their history.');
      refetch();
    }
    setRetireReason('');
    setDeleteBookId(null);
  };

  const generateBook = generateChaptersBookId
    ? books?.find(b => b.id === generateChaptersBookId) ?? null
    : null;

  return (
    <Card>
      <CardHeader className="pb-4">
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="text-lg">Books & Chapters</CardTitle>
            <CardDescription>
              {books?.length ?? 0} books · Manage chapters and capability mappings
            </CardDescription>
          </div>
          <div className="flex items-center gap-2">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search books..."
                value={searchQuery}
                onChange={e => setSearchQuery(e.target.value)}
                className="pl-10 w-[200px]"
              />
            </div>
            <Button onClick={() => setAddBookOpen(true)}>
              <Plus className="h-4 w-4 mr-2" />
              Add Book
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          </div>
        ) : filteredBooks.length === 0 ? (
          <div className="text-center py-12 text-muted-foreground">
            <BookOpen className="h-12 w-12 mx-auto mb-4 opacity-50" />
            <p>No books yet</p>
            <Button variant="outline" className="mt-4" onClick={() => setAddBookOpen(true)}>
              <Plus className="h-4 w-4 mr-2" /> Add First Book
            </Button>
          </div>
        ) : (
          <div className="space-y-2">
            {filteredBooks.map(book => (
              <BookRow
                key={book.id}
                book={book}
                onGenerateChapters={() => setGenerateChaptersBookId(book.id)}
                onDeleteBook={() => setDeleteBookId(book.id)}
                onEditChapterCaps={setCapabilityEditorChapterId}
              />
            ))}
          </div>
        )}
      </CardContent>

      {/* Add Book Dialog */}
      <AddBookDialog
        open={addBookOpen}
        onOpenChange={setAddBookOpen}
        onCreated={() => { refetch(); setAddBookOpen(false); }}
      />

      {/* Generate Chapters Dialog */}
      {generateBook && (
        <GenerateChaptersDialog
          book={generateBook}
          open={!!generateChaptersBookId}
          onOpenChange={open => { if (!open) setGenerateChaptersBookId(null); }}
          onCreated={() => { refetch(); setGenerateChaptersBookId(null); }}
        />
      )}

      {/* Delete Confirmation */}
      <AlertDialog open={!!deleteBookId} onOpenChange={open => { if (!open) setDeleteBookId(null); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Retire Book</AlertDialogTitle>
            <AlertDialogDescription>
              This book and its chapters will no longer be offered. Learners who used them keep their history.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <Input
            aria-label="Reason for retiring"
            placeholder="Reason (required)"
            value={retireReason}
            onChange={e => setRetireReason(e.target.value)}
          />
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDeleteBook} disabled={!retireReason.trim()} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
              Retire
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Capability Editor Dialog */}
      <Dialog open={!!capabilityEditorChapterId} onOpenChange={open => { if (!open) setCapabilityEditorChapterId(null); }}>
        <DialogContent className="max-w-lg max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Map Capabilities</DialogTitle>
            <DialogDescription>Assign capability mappings for this chapter.</DialogDescription>
          </DialogHeader>
          {capabilityEditorChapterId && (
            <ArtifactCapabilityEditor artifactId={capabilityEditorChapterId} />
          )}
        </DialogContent>
      </Dialog>
    </Card>
  );
}

// =============================================
// BOOK ROW (expandable)
// =============================================

function BookRow({
  book,
  onGenerateChapters,
  onDeleteBook,
  onEditChapterCaps,
}: {
  book: BookWithChapters;
  onGenerateChapters: () => void;
  onDeleteBook: () => void;
  onEditChapterCaps: (chapterId: string) => void;
}) {
  return (
    <Collapsible>
      <div className="rounded-lg border">
        <CollapsibleTrigger className="flex items-center justify-between w-full p-3 hover:bg-muted/50 transition-colors group">
          <div className="flex items-center gap-3 min-w-0">
            <ChevronDown className="h-4 w-4 text-muted-foreground transition-transform group-data-[state=open]:rotate-180 shrink-0" />
            <span className="text-lg shrink-0">📖</span>
            <div className="text-left min-w-0">
              <h4 className="text-sm font-medium truncate">{book.title}</h4>
              <div className="flex items-center gap-2 text-xs text-muted-foreground">
                {book.authors && <span>{book.authors}</span>}
                {book.published_year && <span>({book.published_year})</span>}
                {book.isbn_13 && <span className="font-mono">{book.isbn_13}</span>}
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2 shrink-0" onClick={e => e.stopPropagation()}>
            {mappingStatusBadge(book.mapping_status)}
            <Badge variant="outline" className="text-xs">
              {book.chapters.length} ch
            </Badge>
            <Button size="sm" variant="ghost" onClick={onGenerateChapters} title="Generate chapters">
              <ListPlus className="h-4 w-4" />
            </Button>
            <Button size="sm" variant="ghost" onClick={onDeleteBook} title="Retire book">
              <Trash2 className="h-4 w-4 text-muted-foreground hover:text-destructive" />
            </Button>
          </div>
        </CollapsibleTrigger>

        <CollapsibleContent>
          {book.chapters.length === 0 ? (
            <div className="px-4 pb-3 text-sm text-muted-foreground">
              No chapters yet.{' '}
              <button className="text-primary underline" onClick={onGenerateChapters}>
                Generate chapters
              </button>
            </div>
          ) : (
            <div className="px-4 pb-3">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-12">#</TableHead>
                    <TableHead>Title</TableHead>
                    <TableHead className="w-24">Caps</TableHead>
                    <TableHead className="w-16"></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {book.chapters.map(ch => (
                    <TableRow key={ch.id}>
                      <TableCell className="text-muted-foreground font-mono text-xs">{ch.sort_index}</TableCell>
                      <TableCell className="text-sm">{ch.title}</TableCell>
                      <TableCell>
                        <Badge variant="outline" className="text-xs">Map</Badge>
                      </TableCell>
                      <TableCell>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => onEditChapterCaps(ch.id)}
                          title="Edit capability mappings"
                        >
                          <Pencil className="h-3.5 w-3.5" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CollapsibleContent>
      </div>
    </Collapsible>
  );
}

// =============================================
// ADD BOOK DIALOG
// =============================================

function AddBookDialog({
  open,
  onOpenChange,
  onCreated,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onCreated: () => void;
}) {
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    title: '',
    authors: '',
    publisher: '',
    published_year: '',
    edition: '',
    isbn_13: '',
    purchase_url: '',
    purchase_provider_name: '',
    original_summary_short: '',
  });

  const summaryLen = form.original_summary_short.length;

  const handleSave = async () => {
    if (!form.title.trim()) {
      toast.error('Title is required');
      return;
    }

    setSaving(true);
    try {
      // ISBN dedupe check
      if (form.isbn_13.trim()) {
        const { data: existing } = await supabase
          .from('training_artifacts')
          .select('id')
          .eq('isbn_13', form.isbn_13.trim())
          .is('parent_artifact_id', null)
          .limit(1);
        if (existing && existing.length > 0) {
          toast.error('A book with this ISBN-13 already exists');
          setSaving(false);
          return;
        }
      }

      const { error } = await supabase
        .from('training_artifacts')
        .insert({
          title: form.title.trim(),
          artifact_type: 'book',
          provider_name: form.publisher.trim() || 'Books',
          authors: form.authors.trim() || null,
          publisher: form.publisher.trim() || null,
          published_year: form.published_year ? parseInt(form.published_year) : null,
          edition: form.edition.trim() || null,
          isbn_13: form.isbn_13.trim() || null,
          purchase_url: form.purchase_url.trim() || null,
          purchase_provider_name: form.purchase_provider_name.trim() || null,
          original_summary_short: form.original_summary_short.trim() || null,
          access_url: null,
          access_type: 'paid',
          skill_depth: 'applied',
          teaching_styles: ['read_write'],
          delivery_format: 'on_demand',
          travel_required: false,
          artifact_scope: 'domain_foundation',
          confidence_level: 'provisional',
          is_active: true,
          mapping_status: 'draft',
        });

      if (error) throw error;
      toast.success('Book created');
      setForm({ title: '', authors: '', publisher: '', published_year: '', edition: '', isbn_13: '', purchase_url: '', purchase_provider_name: '', original_summary_short: '' });
      onCreated();
    } catch (err) {
      toast.error('Failed to create book');
      console.error(err);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Add Book</DialogTitle>
          <DialogDescription>Add a training book to the catalog.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label>Title *</Label>
            <Input value={form.title} onChange={e => setForm(f => ({ ...f, title: e.target.value }))} />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Authors</Label>
              <Input value={form.authors} onChange={e => setForm(f => ({ ...f, authors: e.target.value }))} placeholder="e.g. John Doe" />
            </div>
            <div>
              <Label>Publisher</Label>
              <Input value={form.publisher} onChange={e => setForm(f => ({ ...f, publisher: e.target.value }))} />
            </div>
          </div>
          <div className="grid grid-cols-3 gap-3">
            <div>
              <Label>Year</Label>
              <Input type="number" value={form.published_year} onChange={e => setForm(f => ({ ...f, published_year: e.target.value }))} />
            </div>
            <div>
              <Label>Edition</Label>
              <Input value={form.edition} onChange={e => setForm(f => ({ ...f, edition: e.target.value }))} placeholder="e.g. 3rd" />
            </div>
            <div>
              <Label>ISBN-13</Label>
              <Input value={form.isbn_13} onChange={e => setForm(f => ({ ...f, isbn_13: e.target.value }))} className="font-mono" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Purchase URL</Label>
              <Input value={form.purchase_url} onChange={e => setForm(f => ({ ...f, purchase_url: e.target.value }))} placeholder="https://..." />
            </div>
            <div>
              <Label>Seller Name</Label>
              <Input value={form.purchase_provider_name} onChange={e => setForm(f => ({ ...f, purchase_provider_name: e.target.value }))} placeholder="e.g. AESwave" />
            </div>
          </div>
          <div>
            <Label>Summary ({summaryLen}/200)</Label>
            <Textarea
              value={form.original_summary_short}
              onChange={e => {
                if (e.target.value.length <= 200) setForm(f => ({ ...f, original_summary_short: e.target.value }));
              }}
              maxLength={200}
              rows={2}
              placeholder="Brief description (≤200 chars)"
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={handleSave} disabled={saving}>
            {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
            Create Book
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// =============================================
// GENERATE CHAPTERS DIALOG
// =============================================

function GenerateChaptersDialog({
  book,
  open,
  onOpenChange,
  onCreated,
}: {
  book: BookWithChapters;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onCreated: () => void;
}) {
  const [mode, setMode] = useState<'paste' | 'manual'>('paste');
  const [pasteText, setPasteText] = useState('');
  const [manualRows, setManualRows] = useState<ParsedChapter[]>([{ number: 1, title: '', summary: '' }]);
  const [preview, setPreview] = useState<ParsedChapter[] | null>(null);
  const [saving, setSaving] = useState(false);

  const parsedFromPaste = useMemo(() => parseChapterList(pasteText), [pasteText]);

  const handlePreview = () => {
    const chapters = mode === 'paste' ? parsedFromPaste : manualRows.filter(r => r.title.trim());
    if (chapters.length === 0) {
      toast.error('No chapters to preview');
      return;
    }
    setPreview(chapters);
  };

  const handleSave = async () => {
    if (!preview || preview.length === 0) return;
    setSaving(true);

    try {
      const rows = preview.map(ch => ({
        title: ch.title,
        artifact_type: 'chapter' as const,
        parent_artifact_id: book.id,
        sort_index: ch.number,
        original_summary_short: ch.summary || null,
        // Inherit from parent book
        provider_name: book.provider_name,
        access_type: book.access_type,
        skill_depth: book.skill_depth,
        teaching_styles: book.teaching_styles,
        delivery_format: book.delivery_format,
        travel_required: book.travel_required,
        artifact_scope: book.artifact_scope,
        confidence_level: book.confidence_level,
        is_active: true,
        access_url: null,
        mapping_status: 'draft',
      }));

      const { error } = await supabase.from('training_artifacts').insert(rows);
      if (error) throw error;

      toast.success(`${preview.length} chapters created`);
      setPreview(null);
      setPasteText('');
      setManualRows([{ number: 1, title: '', summary: '' }]);
      onCreated();
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Failed to create chapters';
      toast.error(msg);
      console.error(err);
    } finally {
      setSaving(false);
    }
  };

  const addManualRow = () => {
    const maxNum = manualRows.reduce((max, r) => Math.max(max, r.number), 0);
    setManualRows(prev => [...prev, { number: maxNum + 1, title: '', summary: '' }]);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Generate Chapters</DialogTitle>
          <DialogDescription>
            Add chapters for <span className="font-medium">{book.title}</span>
          </DialogDescription>
        </DialogHeader>

        {!preview ? (
          <>
            <Tabs value={mode} onValueChange={v => setMode(v as 'paste' | 'manual')}>
              <TabsList>
                <TabsTrigger value="paste">Paste List</TabsTrigger>
                <TabsTrigger value="manual">Manual Entry</TabsTrigger>
              </TabsList>

              <TabsContent value="paste" className="space-y-3">
                <p className="text-xs text-muted-foreground">
                  Paste chapter list — one per line. Format: "1. Title" or "1 - Title"
                </p>
                <Textarea
                  value={pasteText}
                  onChange={e => setPasteText(e.target.value)}
                  rows={10}
                  placeholder={"1. Introduction\n2. Basic Electrical Theory\n3. Using a DVOM\n4. Oscilloscope Fundamentals"}
                />
                {parsedFromPaste.length > 0 && (
                  <p className="text-xs text-muted-foreground">
                    {parsedFromPaste.length} chapters detected
                  </p>
                )}
              </TabsContent>

              <TabsContent value="manual" className="space-y-3">
                <div className="space-y-2">
                  {manualRows.map((row, i) => (
                    <div key={i} className="flex items-center gap-2">
                      <Input
                        type="number"
                        className="w-16 text-center font-mono"
                        value={row.number}
                        onChange={e => {
                          const num = parseInt(e.target.value) || 0;
                          setManualRows(prev => prev.map((r, j) => j === i ? { ...r, number: num } : r));
                        }}
                      />
                      <Input
                        className="flex-1"
                        placeholder="Chapter title"
                        value={row.title}
                        onChange={e => {
                          setManualRows(prev => prev.map((r, j) => j === i ? { ...r, title: e.target.value } : r));
                        }}
                      />
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setManualRows(prev => prev.filter((_, j) => j !== i))}
                        disabled={manualRows.length <= 1}
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                    </div>
                  ))}
                </div>
                <Button variant="outline" size="sm" onClick={addManualRow}>
                  <Plus className="h-3.5 w-3.5 mr-1" /> Add Row
                </Button>
              </TabsContent>
            </Tabs>

            <DialogFooter>
              <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
              <Button onClick={handlePreview}>Preview Chapters</Button>
            </DialogFooter>
          </>
        ) : (
          <>
            <div className="space-y-2">
              <p className="text-sm font-medium">{preview.length} chapters to create:</p>
              <div className="max-h-60 overflow-y-auto rounded border">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-12">#</TableHead>
                      <TableHead>Title</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {preview.map((ch, i) => (
                      <TableRow key={i}>
                        <TableCell className="font-mono text-xs">{ch.number}</TableCell>
                        <TableCell className="text-sm">{ch.title}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setPreview(null)}>Back</Button>
              <Button onClick={handleSave} disabled={saving}>
                {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                Create {preview.length} Chapters
              </Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
